import type { ModalSettings, ModalStore, ToastStore } from '@skeletonlabs/skeleton'
import camelcaseKeys from 'camelcase-keys'
import type { Component } from 'svelte'
import type { FullAutoFill } from 'svelte/elements'
import { get, writable } from 'svelte/store'

import StorageHuggingFaceTokenField from './StorageHuggingFaceTokenField.svelte'
import { encodeHTMLEntities } from '@utils/encode'
import { FetchError, handleFetch } from '@utils/fetch'
import { recommendConvertBranch } from '@utils/hf'
import { modalRegistry } from '@utils/modal-registry.svelte'
import { prepareRestorableModal } from '@utils/modal-restore'

enum StorageType {
  AWS_S3 = 'AWS_S3',
  S3_COMPATIBLE = 'S3_COMPATIBLE',
  GCP = 'GCP',
  Git = 'Git',
  HuggingFace = 'HuggingFace',
  Local = 'Local',
}

const getStorageType = (storageConfig: IRawStorageConfig) => {
  if (storageConfig.s3) {
    return storageConfig.s3.endpointUrl ? StorageType.S3_COMPATIBLE : StorageType.AWS_S3
  }
  if (storageConfig.gcp) {
    return StorageType.GCP
  }
  if (storageConfig.git) {
    return StorageType.Git
  }
  if (storageConfig.huggingface) {
    return StorageType.HuggingFace
  }
  return storageConfig.type
}

const storageTypesValues = Object.values(StorageType)
const userVisibleStorageTypeValues = [
  StorageType.AWS_S3,
  StorageType.S3_COMPATIBLE,
  StorageType.GCP,
  StorageType.Git,
  StorageType.HuggingFace,
]

const displayStorageTypes = {
  [StorageType.AWS_S3]: 'AWS S3',
  [StorageType.S3_COMPATIBLE]: 'S3 Compatible',
  [StorageType.GCP]: 'Google Cloud Storage',
  [StorageType.Git]: 'Git',
  [StorageType.HuggingFace]: 'HuggingFace',
  [StorageType.Local]: 'Local',
}

interface IStorageConfig {
  id: number
  name: string
  type: StorageType
  createdAt: Date
  updatedAt: Date
  organizationId?: number
  s3?: IStorageS3
  gcp?: IStorageGCS
  git?: IStorageGit
  huggingface?: IStorageHuggingFace
  creatorId: number
  creatorName: string
}

type IRawStorageConfig = Omit<IStorageConfig, 'createdAt' | 'updatedAt'> & {
  createdAt: string
  updatedAt: string
}

interface IStorageS3 {
  endpointUrl?: string
  bucketUrl: string
  regionName: string
  accessKeyId: string
  secretAccessKey: string
}

interface IStorageGCS {
  bucketName: string
  project: string
  credentialsJson: string
}

interface IGitRepo {
  id?: number
  repositoryUrl: string
  plainAuth?: {
    username: string
    password: string
  }
  sshAuth?: {
    sshKey: string
    sshPassword: string
  }
}

interface IStorageGit {
  repo: IGitRepo
  branch: string
}

interface IStorageHuggingFace {
  authType?: 'none' | 'token'
  type?: 'dataset' | 'model'
  datasetId?: string
  modelId?: string
  token?: string | null
  tokenId?: number
  branch: string
}

type IStorageConfigPayload = Pick<
  IStorageConfig,
  'name' | 'type' | 'organizationId' | 's3' | 'gcp' | 'git' | 'huggingface'
>

interface ICreateResponse {
  id: number
  createdAt: Date
  updatedAt: Date
}

function isS3(
  storageConfig: unknown,
  type?: StorageType | string | null,
): storageConfig is IStorageS3 {
  return StorageType.S3_COMPATIBLE == type || StorageType.AWS_S3 == type || 'S3' == type
}

function isS3Field(
  storageConfig: unknown,
  type: StorageType | null,
  field: string,
): field is keyof IStorageS3 {
  return isS3(storageConfig, type) && storageConfig && field in storageConfig
}

function isGCS(storageConfig: unknown, type?: StorageType | null): storageConfig is IStorageGCS {
  return StorageType.GCP == type
}

function isGCSField(
  storageConfig: unknown,
  type: StorageType | null,
  field: string,
): field is keyof IStorageGCS {
  return isGCS(storageConfig, type) && storageConfig && field in storageConfig
}

function isGit(storageConfig: unknown, type?: StorageType | null): storageConfig is IStorageGit {
  return StorageType.Git == type
}

function isGitField(
  storageConfig: unknown,
  type: StorageType | null,
  field: string,
): field is keyof IStorageGit {
  return isGit(storageConfig, type)
}

function isHuggingFace(
  storageConfig: unknown,
  type?: StorageType | null,
): storageConfig is IStorageHuggingFace {
  return StorageType.HuggingFace == type
}

function isHuggingFaceField(
  storageConfig: unknown,
  type: StorageType | null,
  field: string,
): field is keyof IStorageHuggingFace {
  return isHuggingFace(storageConfig, type) && storageConfig && field in storageConfig
}

function isValidName(name: unknown): name is string {
  return typeof name === 'string' && name.trim() !== ''
}

function isValidType(type: unknown): type is StorageType {
  return typeof type === 'string' && storageTypesValues.includes(type as StorageType)
}

function isValidOrganizationId(organizationId: unknown): organizationId is number {
  return typeof organizationId === 'number' && organizationId > 0
}

interface IGetPrefix {
  storageInputValues?: Record<string, unknown>
  selectedStorageConfig?: IStorageConfig | null
  type?: StorageType | null
  localStoragePrefix?: string
}
const getStoragePrefix = ({
  storageInputValues,
  selectedStorageConfig,
  type,
  localStoragePrefix,
}: IGetPrefix) => {
  const storageType = type ?? null

  switch (storageType) {
    case StorageType.GCP: {
      if (!isGCS(selectedStorageConfig?.gcp, storageType)) {
        return ''
      }
      if (selectedStorageConfig?.gcp) {
        return `gs://${selectedStorageConfig.gcp.bucketName}/`
      }
      return `gs://${storageInputValues?.bucketName || 'unknown'}/`
    }
    case StorageType.AWS_S3:
    case StorageType.S3_COMPATIBLE: {
      if (!isS3(selectedStorageConfig?.s3, storageType)) {
        return ''
      }
      if (selectedStorageConfig?.s3) {
        return `${selectedStorageConfig.s3.bucketUrl}/`
      }
      return `${!!storageInputValues?.bucketUrl && storageInputValues.bucketUrl != 's3://' ? storageInputValues.bucketUrl : 's3://unknown'}/`
    }
    case StorageType.Git: {
      if (!isGit(selectedStorageConfig?.git, storageType)) {
        return ''
      }
      if (selectedStorageConfig?.git) {
        return `${selectedStorageConfig.git.repo.repositoryUrl}/`
      }
      return `${!!storageInputValues?.repositoryUrl && storageInputValues.repositoryUrl != 'https://' ? storageInputValues.repositoryUrl : 'https://unknown'}/`
    }
    case StorageType.HuggingFace: {
      if (!isHuggingFace(selectedStorageConfig?.huggingface, storageType)) {
        return ''
      }
      if (selectedStorageConfig?.huggingface) {
        if (selectedStorageConfig.huggingface.datasetId) {
          return `hf://datasets/${selectedStorageConfig.huggingface.datasetId}/`
        } else if (selectedStorageConfig.huggingface.modelId) {
          return `hf://models/${selectedStorageConfig.huggingface.modelId}/`
        }
        return `hf://unknown/`
      }
      if (storageInputValues?.datasetId) {
        return `hf://datasets/${storageInputValues.datasetId}/`
      } else if (storageInputValues?.modelId) {
        return `hf://models/${storageInputValues.modelId}/`
      }
      return `hf://unknown/`
    }
    case StorageType.Local: {
      return localStoragePrefix || 'file:///'
    }
    case null: {
      return 'unknown://'
    }
    default: {
      const exhaustiveCheck: never = storageType
      return exhaustiveCheck
    }
  }
}

const storageConfigAsyncState = writable({
  loading: false,
  error: '',
})

const storageConfigStore = writable(new Map<number, IStorageConfig>())

storageConfigStore.subscribe((storageConfigs) => {
  console.log('Storage configs updated: ', storageConfigs)
})

const gitRepoStore = writable(new Map<number, IGitRepo>())
const gitRepoAsyncState = writable({
  loading: false,
  error: '',
})

gitRepoStore.subscribe((gitRepos) => {
  console.log('Git repos updated: ', gitRepos)
})

const parseConfigs = (rawConfigs: IRawStorageConfig): IStorageConfig => {
  const {
    id,
    name,
    type,
    createdAt,
    updatedAt,
    organizationId,
    s3,
    gcp,
    git,
    huggingface,
    creatorId,
    creatorName,
  } = rawConfigs

  return {
    id,
    name,
    type: getStorageType(rawConfigs) ?? type,
    createdAt: new Date(Date.parse(createdAt)),
    updatedAt: new Date(Date.parse(updatedAt)),
    organizationId,
    s3,
    gcp,
    git,
    huggingface,
    creatorId,
    creatorName,
  }
}

interface IGitRepoCreateResponse {
  id: number
}

const convertConfigsForServer = (
  payload: IStorageConfigPayload,
  gitRepoResponse: IGitRepoCreateResponse | null,
): Record<string, unknown> => ({
  ...payload,
  type:
    payload.type == StorageType.S3_COMPATIBLE
      ? 'S3'
      : payload.type == StorageType.AWS_S3
        ? 'S3'
        : payload.type,
  ...(payload.git && gitRepoResponse && gitRepoResponse.id
    ? { git: { repoId: gitRepoResponse.id, branch: payload.git.branch } }
    : payload.git
      ? { git: { repoId: payload.git.repo.id, branch: payload.git.branch } }
      : {}),
})

const loadGitRepos = async () => {
  try {
    const gitRepos = camelcaseKeys(await (await handleFetch('/git-repo/')).json(), { deep: true })
    gitRepoStore.set(
      gitRepos.reduce((acc: Map<number, IGitRepo>, gitRepo: IGitRepo) => {
        if (gitRepo.id) {
          acc.set(gitRepo.id, gitRepo)
        }
        return acc
      }, new Map()),
    )
    gitRepoAsyncState.set({
      loading: false,
      error: '',
    })
  } catch (err) {
    console.error('Failed to load git repos: ', err)
    gitRepoAsyncState.set({
      loading: false,
      error: 'Failed to load git repos',
    })
  }
}

const loadStorageConfig = async () => {
  storageConfigAsyncState.set({
    loading: true,
    error: '',
  })
  type IRawStorageConfig = IStorageConfig & { createdAt: string; updatedAt: string }
  try {
    const storageConfigs: IRawStorageConfig[] = camelcaseKeys(
      await (await handleFetch('/storage-config/')).json(),
      { deep: true },
    )
    if (storageConfigs.some((config) => config.git)) {
      await loadGitRepos()
    }
    const storageConfigMap = storageConfigs.reduce((acc, storageConfig) => {
      acc.set(storageConfig.id, parseConfigs(storageConfig))
      return acc
    }, new Map<number, IStorageConfig>())
    storageConfigStore.set(storageConfigMap)
    storageConfigAsyncState.set({
      loading: false,
      error: '',
    })
  } catch (err) {
    console.error('Failed to load storage configs: ', err)
    storageConfigAsyncState.set({
      loading: false,
      error: 'Failed to load storage configs',
    })
  }
}

interface IBaseField {
  fieldName: string
  label: string
  type: string
  required?: boolean
  showField?: (inputValues: Record<string, unknown>, creating?: 'dataset' | 'model') => boolean
}

interface ITextField extends IBaseField {
  type: 'text' | 'password' | 'url'
  pattern?: RegExp
  patternError?: string
  autocomplete?: FullAutoFill
  default?: string
}

const isSelectField = (field: IField): field is ISelectField => {
  return field.type === 'select'
}

const isTextField = (field: IField): field is ITextField => {
  return field.type === 'text' || field.type === 'password' || field.type === 'url'
}

interface IFileField extends IBaseField {
  type: 'file'
  accept: string
}

interface ISelectField extends IBaseField {
  type: 'select'
  options: Record<string, string>
  getInitialSelectedOptionValue: (
    inputValues: Record<string, unknown>,
    creating?: 'dataset' | 'model',
  ) => string
}

interface ISpecialField extends IBaseField {
  type: 'special'
  component: Component<{
    inputValues: { [key: string]: string | undefined | null }
  }>
}

const isSpecialField = (field: IField): field is ISpecialField => {
  return field.type === 'special'
}

type IField = ITextField | IFileField | ISelectField | ISpecialField

const s3SharedFields: ITextField[] = [
  {
    fieldName: 'bucketUrl',
    label: '*Bucket URL',
    type: 'text',
    required: true,
    // The pattern uses a `-` in the first match group, which in modern browsers which add the `/v` flag will be interpreted as a range.
    // This is a workaround to ensure the pattern works in all browsers.
    // eslint-disable-next-line no-useless-escape
    pattern: /^[a-z0-9.\-]{3,64}\/?$/,
    patternError: 'Invalid S3 bucket URL',
  },
  { fieldName: 'regionName', label: '*Region name', type: 'text', required: true },
  { fieldName: 'accessKeyId', label: 'Access key ID', type: 'text' },
  {
    fieldName: 'secretAccessKey',
    label: 'Secret access key',
    type: 'password',
    autocomplete: 'off',
  },
]

const storageFields: Record<StorageType, IField[]> = {
  [StorageType.AWS_S3]: [...s3SharedFields],
  [StorageType.S3_COMPATIBLE]: [
    { fieldName: 'endpointUrl', label: 'Endpoint URL', type: 'url' },
    ...s3SharedFields,
  ],
  [StorageType.GCP]: [
    {
      fieldName: 'bucketName',
      label: '*Bucket name',
      type: 'text',
      required: true,
      // The pattern uses a `-` in the first match group, which in modern browsers which add the `/v` flag will be interpreted as a range.
      // This is a workaround to ensure the pattern works in all browsers.
      // eslint-disable-next-line no-useless-escape
      pattern: /^([a-z0-9][a-z0-9_\-]{1,61}[a-z0-9](\.[a-z0-9][a-z0-9_\-]{1,61}[a-z0-9])*)$/,
      patternError: 'Invalid Google Cloud Storage bucket name',
    },
    { fieldName: 'project', label: '*Project', type: 'text', required: true },
    {
      fieldName: 'credentialsJson',
      label: 'Credentials JSON',
      type: 'file',
      accept: 'application/json',
    },
  ],
  [StorageType.Git]: [
    { fieldName: 'repo.repositoryUrl', label: '*Repository URL', type: 'url', required: true },
    {
      fieldName: 'repo.authType',
      label: 'Auth type',
      type: 'select',
      required: true,
      options: {
        none: 'None',
        plain: 'Plain',
        ssh: 'SSH',
      },
      getInitialSelectedOptionValue: (inputValues) =>
        (inputValues['repo.authType'] as string | undefined) || 'none',
    },
    {
      fieldName: 'repo.plainAuth.username',
      label: '*Username',
      type: 'text',
      required: true,
      showField: (inputValues) => inputValues['repo.authType'] === 'plain',
    },
    {
      fieldName: 'repo.plainAuth.password',
      label: '*Password',
      type: 'password',
      required: true,
      showField: (inputValues) => inputValues['repo.authType'] === 'plain',
      autocomplete: 'off',
    },
    {
      fieldName: 'repo.sshAuth.sshKey',
      label: '*SSH key',
      type: 'file',
      accept: 'application/x-pem-file',
      required: true,
      showField: (inputValues) => inputValues['repo.authType'] === 'ssh',
    },
    {
      fieldName: 'repo.sshAuth.sshPassword',
      label: '*SSH password',
      type: 'password',
      showField: (inputValues) => inputValues['repo.authType'] === 'ssh',
      autocomplete: 'off',
    },
    { fieldName: 'branch', label: '*Branch', type: 'text', required: true, default: 'main' },
  ],
  [StorageType.HuggingFace]: [
    {
      fieldName: 'type',
      label: 'Type',
      type: 'select',
      options: {
        dataset: 'Dataset',
        model: 'Model',
      },
      getInitialSelectedOptionValue: (inputValues, creating) =>
        creating === 'dataset' ||
        (inputValues['datasetId'] !== undefined && inputValues['datasetId'] !== null)
          ? 'dataset'
          : 'model',
      required: true,
      showField: (_inputValues, creating) => !creating,
    },
    {
      fieldName: 'datasetId',
      label: '*Dataset ID',
      type: 'text',
      required: true,
      showField: (inputValues, creating) =>
        creating === 'dataset' || inputValues['type'] === 'dataset',
    },
    {
      fieldName: 'modelId',
      label: '*Model ID',
      type: 'text',
      required: true,
      showField: (inputValues, creating) => creating === 'model' || inputValues['type'] === 'model',
    },
    {
      fieldName: 'authType',
      label: 'Auth type',
      type: 'select',
      required: true,
      options: {
        none: 'None',
        token: 'Token',
      },
      getInitialSelectedOptionValue: (inputValues) =>
        (inputValues['authType'] as string | undefined) || 'none',
    },
    {
      fieldName: 'token',
      label: '*HuggingFace token',
      type: 'special',
      required: true,
      showField: (inputValues) => inputValues['authType'] === 'token',
      component: StorageHuggingFaceTokenField,
    },
    { fieldName: 'branch', label: '*Branch', type: 'text', required: true, default: 'main' },
  ],
  [StorageType.Local]: [],
}

const getPattern = (field: IField) => {
  if ('pattern' in field) {
    return field.pattern?.source
  }
}

const normalizeNumericTokenId = (tokenId: unknown) => {
  if (typeof tokenId === 'number' && Number.isInteger(tokenId)) {
    return tokenId
  }
  if (typeof tokenId === 'string' && /^\d+$/.test(tokenId)) {
    return parseInt(tokenId, 10)
  }
  return undefined
}

const createStorageConfig = async (
  payload: IStorageConfigPayload,
  toastStore: ToastStore,
  creatorId: number,
  creatorName: string,
): Promise<ICreateResponse> => {
  try {
    if (
      payload.type === StorageType.HuggingFace &&
      payload.huggingface?.datasetId &&
      payload.huggingface.branch
    ) {
      await recommendConvertBranch({
        datasetId: payload.huggingface.datasetId,
        currentBranch: payload.huggingface.branch,
        token: payload.huggingface.token ?? null,
        toastStore,
        confirm:
          typeof window !== 'undefined' ? (message: string) => window.confirm(message) : undefined,
        onApplyBranch: (branch) => {
          if (payload.huggingface) {
            payload.huggingface.branch = branch
          }
        },
      })
    }

    const gitRepoResponse = payload.git
      ? await (
          await handleFetch('/git-repo/', {
            method: 'POST',
            body: {
              name: payload.name,
              repositoryUrl: payload.git.repo.repositoryUrl,
              organizationId: payload.organizationId,
              plainAuth: payload.git.repo.plainAuth?.username ? payload.git.repo.plainAuth : null,
              sshAuth: payload.git.repo.sshAuth?.sshKey ? payload.git.repo.sshAuth : null,
            },
          })
        ).json()
      : null
    const response = await handleFetch(`/storage-config/`, {
      method: 'POST',
      body: convertConfigsForServer(payload, gitRepoResponse),
    })

    const rawStorageConfig = camelcaseKeys(await response.json())
    storageConfigStore.update(($storageConfigStore) => {
      if (!isValidName(payload.name)) {
        toastStore.trigger({
          message: 'Storage config is missing a valid name',
          background: 'variant-filled-error',
        })
        throw new Error('Invalid name')
      }

      if (!isValidType(payload.type)) {
        toastStore.trigger({
          message: 'Storage config is missing a valid type',
          background: 'variant-filled-error',
        })
        throw new Error('Invalid type')
      }

      if (!isValidOrganizationId(payload.organizationId)) {
        toastStore.trigger({
          message: 'Storage config is missing a valid organization ID',
          background: 'variant-filled-error',
        })
        throw new Error('Invalid organization ID')
      }

      const { name, type, organizationId } = payload

      let s3: IStorageS3 | undefined
      let gcp: IStorageGCS | undefined
      let git: IStorageGit | undefined
      let huggingface: IStorageHuggingFace | undefined

      switch (type) {
        case StorageType.AWS_S3:
        case StorageType.S3_COMPATIBLE: {
          s3 = payload.s3
          break
        }
        case StorageType.GCP: {
          gcp = payload.gcp
          break
        }
        case StorageType.Git: {
          git = payload.git
          break
        }
        case StorageType.HuggingFace: {
          huggingface = payload.huggingface
          break
        }
        case StorageType.Local: {
          break
        }
        default: {
          const exhaustiveCheck: never = type
          toastStore.trigger({
            message: 'Storage config type is unrecognized',
            background: 'variant-filled-error',
          })
          return exhaustiveCheck
        }
      }
      $storageConfigStore.set(
        rawStorageConfig.id,
        parseConfigs({
          id: rawStorageConfig.id,
          name,
          type,
          organizationId,
          s3,
          gcp,
          git,
          huggingface,
          createdAt: rawStorageConfig.createdAt,
          updatedAt: rawStorageConfig.updatedAt,
          creatorId,
          creatorName,
        }),
      )
      return $storageConfigStore
    })
    toastStore.trigger({
      message: `Created storage config named <strong>${encodeHTMLEntities(payload.name)}</strong>`,
      background: 'variant-filled-success',
    })

    return rawStorageConfig // return the config if needed for further use
  } catch (err) {
    console.error(err)
    if (err instanceof FetchError && err.message) {
      toastStore.trigger({
        message: encodeHTMLEntities(`Failed to create storage config: ${err.message}`),
        background: 'variant-filled-error',
        autohide: false,
      })
      throw err
    }
    toastStore.trigger({
      message: 'Failed to create storage config',
      background: 'variant-filled-error',
    })
    throw err
  }
}

const deleteStorageConfig = async (storageConfigId: number) => {
  if (!storageConfigId) {
    console.error('Refusing to delete an undefined storage config ID')
    return
  }

  await handleFetch(`/storage-config/${storageConfigId}`, {
    method: 'DELETE',
  })

  storageConfigStore.update((store) => {
    store.delete(storageConfigId)
    return store
  })
}

const openCreateStorageConfigModal = (
  modalStore: ModalStore,
  toastStore: ToastStore,
  creatorId: number,
  creatorName: string,
) => {
  const modal: ModalSettings = {
    type: 'component',
    component: modalRegistry.storageFormModal,
    title: 'Create storage config',
    body: 'Please fill in the details to create a new storage config.',
    buttonTextSubmit: 'Create',
    meta: {},
    response: async (response) => {
      if (!response) {
        return
      }
      const { payload, closeModal } = response
      await createStorageConfig(payload, toastStore, creatorId, creatorName)
      closeModal()
    },
  }
  modalStore.trigger(
    prepareRestorableModal(modalStore, toastStore, modal, {
      toastMessage: 'Create storage config closed. Reopen to restore your changes?',
    }),
  )
}

const editStorageConfig = async (
  payload: IStorageConfigPayload,
  storageConfigId: number,
  toastStore: ToastStore,
) => {
  try {
    if (
      payload.type === StorageType.HuggingFace &&
      payload.huggingface?.datasetId &&
      payload.huggingface.branch
    ) {
      await recommendConvertBranch({
        datasetId: payload.huggingface.datasetId,
        currentBranch: payload.huggingface.branch,
        token: payload.huggingface.token ?? null,
        toastStore,
        confirm:
          typeof window !== 'undefined' ? (message: string) => window.confirm(message) : undefined,
        onApplyBranch: (branch) => {
          if (payload.huggingface) {
            payload.huggingface.branch = branch
          }
        },
      })
    }

    const gitRepoResponse =
      payload.git?.repo.id && payload.git
        ? await (
            await handleFetch(`/git-repo/${payload.git.repo.id}`, {
              method: 'PUT',
              body: {
                name: payload.name,
                repositoryUrl: payload.git.repo.repositoryUrl,
                organizationId: payload.organizationId,
                plainAuth: payload.git.repo.plainAuth?.username ? payload.git.repo.plainAuth : null,
                sshAuth: payload.git.repo.sshAuth?.sshKey ? payload.git.repo.sshAuth : null,
              },
            })
          ).json()
        : null
    const response = await handleFetch(`/storage-config/${storageConfigId}`, {
      method: 'PUT',
      body: convertConfigsForServer(payload, gitRepoResponse),
    })
    const rawStorageConfig = camelcaseKeys(await response.json())
    storageConfigStore.update(($storageConfigStore) => {
      const existingStorageConfig = $storageConfigStore.get(storageConfigId)
      if (!existingStorageConfig) {
        console.error('Existing storage config not found')
        return $storageConfigStore
      }
      const { id, type, createdAt, creatorId, creatorName } = existingStorageConfig

      if (!isValidName(payload.name)) {
        toastStore.trigger({
          message: 'Storage config is missing a valid name',
          background: 'variant-filled-error',
        })
        return $storageConfigStore
      }

      if (!isValidOrganizationId(payload.organizationId)) {
        toastStore.trigger({
          message: 'Storage config is missing a valid organization ID',
          background: 'variant-filled-error',
        })
        return $storageConfigStore
      }

      const { name, organizationId } = payload

      let s3: IStorageS3 | undefined
      let gcp: IStorageGCS | undefined
      let git: IStorageGit | undefined
      let huggingface: IStorageHuggingFace | undefined

      if (isS3(payload.s3, type)) {
        s3 = payload.s3
      } else if (isGCS(payload.gcp, type)) {
        gcp = payload.gcp
      } else if (isGit(payload.git, type)) {
        git = payload.git
      } else if (isHuggingFace(payload.huggingface, type)) {
        huggingface = payload.huggingface
      } else if (type !== StorageType.Local) {
        toastStore.trigger({
          message: 'Storage config type is unrecognized',
          background: 'variant-filled-error',
        })
        return $storageConfigStore
      }

      $storageConfigStore.set(
        storageConfigId,
        parseConfigs({
          id,
          name,
          type,
          organizationId,
          s3,
          gcp,
          git,
          huggingface,
          createdAt: createdAt.toString(),
          updatedAt: rawStorageConfig.updatedAt,
          creatorId,
          creatorName,
        }),
      )
      return $storageConfigStore
    })
    toastStore.trigger({
      message: `Updated storage config named <strong>${encodeHTMLEntities(payload.name)}</strong>`,
      background: 'variant-filled-success',
    })

    return rawStorageConfig
  } catch (err) {
    console.error('Failed to edit storage config', err)
    if (err instanceof FetchError && err.message) {
      toastStore.trigger({
        message: encodeHTMLEntities(`Failed to edit storage config: ${err.message}`),
        background: 'variant-filled-error',
        autohide: false,
      })
      return
    }
    toastStore.trigger({
      message: 'Failed to edit storage config',
      background: 'variant-filled-error',
    })
  }
}

const openEditStorageConfigModal =
  (modalStore: ModalStore, toastStore: ToastStore, storageConfigId: number) => () => {
    const storageConfigToEdit = get(storageConfigStore).get(storageConfigId)
    if (!storageConfigToEdit) {
      console.error('Storage config not found')
      return
    }
    if (storageConfigToEdit.git?.repo.id) {
      const repo = get(gitRepoStore).get(storageConfigToEdit.git.repo.id)
      if (repo) {
        storageConfigToEdit.git.repo = repo
      } else {
        console.error('Git repo not found')
        return
      }
    }

    const modal: ModalSettings = {
      type: 'component',
      component: modalRegistry.storageFormModal,
      title: 'Edit storage config',
      body: 'Please update the storage config details.',
      buttonTextSubmit: 'Save',
      meta: { storageConfigId },
      value: storageConfigToEdit,
      response: async (response) => {
        if (!response) {
          return
        }
        const { payload, closeModal } = response
        await editStorageConfig(payload, storageConfigId, toastStore)
        closeModal()
      },
    }
    modalStore.trigger(
      prepareRestorableModal(modalStore, toastStore, modal, {
        toastMessage: 'Edit storage config closed. Reopen to restore your changes?',
      }),
    )
  }

const prepareInputValuesForQuery = (
  inputValues: Record<string, unknown>,
  selectedStorageType: StorageType,
): Partial<IStorageConfig> => {
  const payload: Partial<IStorageConfig> = {}

  switch (selectedStorageType) {
    case StorageType.AWS_S3:
    case StorageType.S3_COMPATIBLE: {
      if (!isS3(inputValues, selectedStorageType)) {
        break
      }

      const {
        accessKeyId = '',
        secretAccessKey = '',
        regionName = '',
        bucketUrl = '',
        endpointUrl = null,
      } = inputValues

      const editedEndpointUrl = endpointUrl === '' ? null : endpointUrl

      payload.s3 = {
        accessKeyId,
        secretAccessKey,
        regionName,
        bucketUrl,
        endpointUrl: editedEndpointUrl as string,
      }
      break
    }
    case StorageType.GCP: {
      if (!isGCS(inputValues, selectedStorageType)) {
        break
      }

      const { bucketName, project, credentialsJson } = inputValues

      payload.gcp = {
        bucketName,
        project,
        credentialsJson,
      }
      break
    }
    case StorageType.Git: {
      if (!isGit(inputValues, selectedStorageType)) {
        break
      }

      payload.git = {
        repo: {
          repositoryUrl: inputValues['repo.repositoryUrl'] as string,
          ...(inputValues['repo.plainAuth.username']
            ? {
                plainAuth: {
                  username: inputValues['repo.plainAuth.username'] as string,
                  password: inputValues['repo.plainAuth.password'] as string,
                },
              }
            : {}),
          ...(inputValues['repo.sshAuth.sshKey']
            ? {
                sshAuth: {
                  sshKey: inputValues['repo.sshAuth.sshKey'] as string,
                  sshPassword: inputValues['repo.sshAuth.sshPassword'] as string,
                },
              }
            : {}),
        },
        branch: inputValues['branch'],
      }
      break
    }
    case StorageType.HuggingFace: {
      if (!isHuggingFace(inputValues, selectedStorageType)) {
        break
      }

      const { authType, datasetId, modelId, token, tokenId, branch } = inputValues

      payload.huggingface = {
        datasetId,
        modelId,
        authType: authType === 'token' ? 'token' : 'none',
        token: authType === 'token' && token ? token : null,
        tokenId: authType === 'token' ? normalizeNumericTokenId(tokenId) : undefined,
        branch,
      }
      break
    }
    case StorageType.Local: {
      // Local storage does not require additional configuration fields
      break
    }
    default: {
      const exhaustiveCheck: never = selectedStorageType
      return exhaustiveCheck
    }
  }

  return payload
}

export type {
  IGitRepo,
  IRawStorageConfig,
  ISpecialField,
  IStorageConfig,
  IStorageGCS,
  IStorageGit,
  IStorageS3,
  ITextField,
}
export {
  createStorageConfig,
  deleteStorageConfig,
  displayStorageTypes,
  getPattern,
  getStoragePrefix,
  isGCS,
  isGCSField,
  isGit,
  isGitField,
  isHuggingFace,
  isHuggingFaceField,
  isS3,
  isS3Field,
  isSelectField,
  isSpecialField,
  isTextField,
  loadStorageConfig,
  openCreateStorageConfigModal,
  openEditStorageConfigModal,
  parseConfigs,
  prepareInputValuesForQuery,
  storageConfigAsyncState,
  storageConfigStore,
  storageFields,
  StorageType,
  storageTypesValues,
  userVisibleStorageTypeValues,
}
